import { useEffect, useState } from "react";
import type { OtpStrategy } from "@codway-dev/shared";
import type { CallbackEvent } from "@shopify/polaris-types";
import { useOtpConfigStore } from "../../store/useOtpConfigStore";

const VALIDITY_OPTIONS = [
  { value: 1, label: "1 hora" },
  { value: 6, label: "6 horas" },
  { value: 12, label: "12 horas" },
  { value: 24, label: "24 horas (predeterminado)" },
  { value: 48, label: "48 horas" },
  { value: 72, label: "72 horas" },
  { value: 168, label: "168 horas (7 días)" },
  { value: 720, label: "720 horas (30 días)" },
];

function ClampedNumberField({
  label,
  min,
  max,
  value,
  onCommit,
}: {
  label?: string;
  min: number;
  max: number;
  value: number;
  onCommit: (n: number) => void;
}) {
  const [raw, setRaw] = useState(String(value));

  // Sync local state when the store value changes externally (e.g. discard).
  useEffect(() => {
    setRaw(String(value));
  }, [value]);

  return (
    <s-number-field
      {...(label ? { label } : {})}
      min={min}
      max={max}
      value={raw}
      onInput={(e: CallbackEvent<"s-number-field">) =>
        setRaw(e.currentTarget.value)
      }
      onBlur={() => {
        const n = Math.min(max, Math.max(min, parseInt(raw, 10) || min));
        setRaw(String(n));
        onCommit(n);
      }}
    />
  );
}

export function OtpConfigForm() {
  const isEnabled = useOtpConfigStore((s) => s.isEnabled);
  const channel = useOtpConfigStore((s) => s.channel);
  const strategy = useOtpConfigStore((s) => s.strategy);
  const minOrderValueForOtp = useOtpConfigStore((s) => s.minOrderValueForOtp);
  const validityPeriodHours = useOtpConfigStore((s) => s.validityPeriodHours);
  const maxResends = useOtpConfigStore((s) => s.maxResends);
  const maxVerifyAttempts = useOtpConfigStore((s) => s.maxVerifyAttempts);
  const smsTemplate = useOtpConfigStore((s) => s.smsTemplate);
  const blockVoipNumbers = useOtpConfigStore((s) => s.blockVoipNumbers);
  const updateField = useOtpConfigStore((s) => s.updateField);

  const showSmsTemplate =
    channel === "sms" || channel === "whatsapp_fallback_sms";

  return (
    <s-stack direction="block" gap="large-400">
      <s-section heading="Configuración general">
        <s-stack direction="block" gap="large-200">
          <s-checkbox
            label="Activar verificación por teléfono (OTP)"
            checked={isEnabled}
            onChange={(e: CallbackEvent<"s-checkbox">) => {
              const checked = e.currentTarget.checked;
              updateField("isEnabled", checked);
              if (checked && strategy === "disabled") {
                updateField("strategy", "always");
              }
            }}
          />

          <s-divider />

          <s-stack direction="block" gap="small-200">
            <s-text>Selecciona un canal</s-text>
            <s-box inlineSize="auto" maxInlineSize="280px">
              <s-select
                value={channel}
                onChange={(e: CallbackEvent<"s-select">) =>
                  updateField(
                    "channel",
                    e.currentTarget.value as typeof channel
                  )
                }
              >
                <s-option value="sms">SMS</s-option>
                <s-option value="whatsapp">WhatsApp</s-option>
                <s-option value="whatsapp_fallback_sms">
                  WhatsApp fallback SMS
                </s-option>
              </s-select>
            </s-box>
            <s-text color="subdued">
              Elige cómo enviar mensajes a tus clientes.
            </s-text>
          </s-stack>

          {channel === "whatsapp_fallback_sms" && (
            <s-banner tone="info" dismissible>
              Con esta opción seleccionada, los usuarios con WhatsApp activado
              recibirán mensajes vía WhatsApp siempre que tengan acceso a
              Internet. Si no tienen acceso a Internet, recibirán mensajes vía
              SMS como fallback después de 30 segundos. Se te cobrará dos veces:
              una vez por SMS y una vez por WhatsApp. Si el usuario no tiene
              WhatsApp activado, recibirá mensajes SMS inmediatamente y solo se
              te cobrará por SMS.
            </s-banner>
          )}

          {isEnabled && (
            <>
              <s-divider />

              <s-stack direction="block" gap="small-200">
                <s-text>Estrategia — ¿cuándo se pide OTP?</s-text>
                <s-box inlineSize="auto" maxInlineSize="280px">
                  <s-select
                    value={strategy}
                    onChange={(e: CallbackEvent<"s-select">) =>
                      updateField(
                        "strategy",
                        e.currentTarget.value as OtpStrategy
                      )
                    }
                  >
                    <s-option value="always">Siempre</s-option>
                    <s-option value="high_risk_only">Solo alto riesgo</s-option>
                    <s-option value="order_value_high">
                      Valor de pedido alto
                    </s-option>
                    <s-option value="whatsapp_silent">
                      WhatsApp silencioso
                    </s-option>
                  </s-select>
                </s-box>
                {strategy === "order_value_high" && (
                  <s-box inlineSize="auto" maxInlineSize="160px">
                    <s-number-field
                      label="Umbral de valor de pedido"
                      min={0}
                      value={String(minOrderValueForOtp ?? 0)}
                      onInput={(e: CallbackEvent<"s-number-field">) =>
                        updateField(
                          "minOrderValueForOtp",
                          Math.max(0, parseFloat(e.currentTarget.value) || 0)
                        )
                      }
                    />
                  </s-box>
                )}
              </s-stack>
            </>
          )}
        </s-stack>
      </s-section>

      <s-section heading="Mensaje y límites">
        <s-stack direction="block" gap="large-200">
          {showSmsTemplate && (
            <s-stack direction="block" gap="small-200">
              <s-text-area
                label="Texto mensaje"
                value={smsTemplate}
                maxLength={140}
                onInput={(e: CallbackEvent<"s-text-area">) =>
                  updateField("smsTemplate", e.currentTarget.value)
                }
              />
              <s-text color="subdued">
                Use {"{otp_code}"} para insertar el código OTP dinámicamente en
                este campo de texto. No sugerimos cambiar el mensaje
                predeterminado. <strong>Importante:</strong> no se permiten
                enlaces dentro del texto del mensaje.
              </s-text>
              {!smsTemplate.includes("{otp_code}") && (
                <s-text tone="critical">
                  Falta el marcador {"{otp_code}"} — sin él, el SMS no incluirá
                  el código de verificación.
                </s-text>
              )}
            </s-stack>
          )}

          <s-stack direction="inline" gap="large-400">
            <s-stack direction="block" gap="small-200">
              <s-text>Número máximo de reenvíos permitidos:</s-text>
              <s-box inlineSize="auto" maxInlineSize="120px">
                <ClampedNumberField
                  min={1}
                  max={10}
                  value={maxResends}
                  onCommit={(n) => updateField("maxResends", n)}
                />
              </s-box>
            </s-stack>

            <s-stack direction="block" gap="small-200">
              <s-text>Número máximo de intentos de código permitidos:</s-text>
              <s-box inlineSize="auto" maxInlineSize="120px">
                <ClampedNumberField
                  min={1}
                  max={10}
                  value={maxVerifyAttempts}
                  onCommit={(n) => updateField("maxVerifyAttempts", n)}
                />
              </s-box>
            </s-stack>
          </s-stack>

          <s-divider />

          <s-stack direction="block" gap="small-200">
            <s-text>Período de validez de la verificación</s-text>
            <s-text color="subdued">
              ¿Cuánto tiempo debe permanecer válido un número de teléfono
              verificado?
            </s-text>
            <s-box inlineSize="auto" maxInlineSize="280px">
              <s-select
                value={String(validityPeriodHours)}
                onChange={(e: CallbackEvent<"s-select">) =>
                  updateField(
                    "validityPeriodHours",
                    parseInt(e.currentTarget.value, 10)
                  )
                }
              >
                {VALIDITY_OPTIONS.map((opt) => (
                  <s-option key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </s-option>
                ))}
              </s-select>
            </s-box>
          </s-stack>
        </s-stack>
      </s-section>

      <s-section heading="Reglas de seguridad">
        <s-stack direction="block" gap="small-200">
          <s-checkbox
            label="Bloquear números VOIP / líneas fijas"
            details="Rechaza números que no sean líneas móviles reales."
            checked={blockVoipNumbers}
            onChange={(e: CallbackEvent<"s-checkbox">) =>
              updateField("blockVoipNumbers", e.currentTarget.checked)
            }
          />
        </s-stack>
      </s-section>
    </s-stack>
  );
}
